import axios, { AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken, logout, setAccessToken, setMediaToken, setRefreshToken } from '../../apps/legacy-app/src/utils/storage';
import { LoginResponse } from './models/auth/LoginResponse';
import { RefreshTokenRequest } from './models/auth/RefreshTokenRequest';

const BASE_URL = import.meta.env.VITE_API_URL as string;
const USE_SUBDOMAIN = import.meta.env.VITE_USE_SUBDOMAIN as string;
const REFRESH_PATH = '/api/v1/auth/refresh';

export const BASE_URL_WITH_VERSION = `${BASE_URL}/api/v1`;

export const api = axios.create({
  baseURL: BASE_URL,
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    config.headers = new AxiosHeaders({
      ...config.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(USE_SUBDOMAIN !== 'true' ? { 'x-skip-validation': '4321' } : {}),
    });

    return config;
  },
  (error) => Promise.reject(error),
);

const getRequestAccessToken = (config: InternalAxiosRequestConfig): string | undefined => {
  const header = config.headers?.get?.('Authorization') ?? config.headers?.Authorization;

  return typeof header === 'string' ? header.replace('Bearer ', '') : undefined;
};

const isSessionRejected = (error: unknown) => axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403);

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean; _dontRedirect?: boolean };

const redirectToLogin = () => {
  // Without clearing the cookies the reloaded app finds the token, asks for the identity, gets the same
  // 401 and redirects again - an endless reload loop that never reaches the login form.
  logout();
  window.location.href = '/login';

  // The document is being replaced, so nothing is left to handle a rejection - stay pending.
  return new Promise<never>(() => undefined);
};

const retryWithFreshToken = async (originalRequest: RetryableRequest) => {
  const token = getAccessToken();

  if (!token || originalRequest._retry) return redirectToLogin();
  originalRequest._retry = true;

  // Another request or tab already refreshed while this one was on the wire - just retry.
  if (token !== getRequestAccessToken(originalRequest)) return api(originalRequest);

  try {
    await refreshToken();

    // Deliberately not awaited: a 401 on the retry itself is the caller's, not a refresh failure.
    return api(originalRequest);
  } catch (refreshError) {
    // Connectivity trouble says nothing about the session - keep the user where they are.
    if (!isSessionRejected(refreshError)) throw refreshError;

    return redirectToLogin();
  }
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest: RetryableRequest | undefined = error.config;

    if (error.response?.status !== 401 || !originalRequest || originalRequest._dontRedirect) return Promise.reject(error);

    return retryWithFreshToken(originalRequest);
  },
);

// Refreshing has to bypass `api`: a 401 on the refresh itself would re-enter the interceptor above.
const refreshApi = axios.create({
  baseURL: BASE_URL,
});

let pendingRefresh: Promise<void> | null = null;

const requestNewTokens = async () => {
  const body: RefreshTokenRequest = { refreshToken: getRefreshToken()! };
  const res = await refreshApi.post<LoginResponse>(REFRESH_PATH, body);

  setAccessToken(res.data.accessToken!);
  setRefreshToken(res.data.refreshToken!);
  setMediaToken(res.data.mediaToken!);
};

// The backend rotates the refresh token, so parallel 401s have to share one in-flight refresh.
export const refreshToken = async () => {
  if (!pendingRefresh) {
    pendingRefresh = requestNewTokens().finally(() => {
      pendingRefresh = null;
    });
  }

  return pendingRefresh;
};
